import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import dayjs from "dayjs";
import { Box, Typography, IconButton, Chip, Drawer } from "@mui/material";
import { Sheet } from "react-modal-sheet";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useApiClient from "../../api/Client";
import PlaceCard from "./PlaceCard";
import GenericCardSkeleton from "@/shared/components/GenericCardSkeleton";
import PlaceDetailsSkeleton from "./PlaceDetailsSkeleton";
import PlaceDetails from "./PlaceDetails";
import AccessibilityOptions from "./AccessibilityOptions";
import ENDPOINTS from "@/api/endpoints";
import { RemoveCircleOutlineRounded } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store";
import { Place } from "@/types/common";
import {
  clearPlaceFilter,
  setDrawerOpen,
} from "@/store/slices/placeFilterSlice";
import { clearSelectedPlace } from "@/store/slices/selectedPlaceSlice";
import ActionButtonGroup from "./ActionButtonGroup";
import { ACCESSIBILITY_STATUS_CONFIG } from "@/shared/constants/accessibility";

interface GoogleNearbyPlace {
  id: string;
  location?: { latitude: number; longitude: number };
  types?: string[];
  displayName?: { text: string };
  formattedAddress?: string;
  types_mapped?: {
    category_id?: string;
    category?: string;
    subcategory_id?: string;
    subcategory?: string;
  };
}

interface FilterDrawerProps {
  userPosition?: {
    coords: {
      latitude: number;
      longitude: number;
    };
  };
}

const styles = {
  sheetContainer: {
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    backgroundColor: "#fff",
    boxShadow: "0px -5px 20px rgba(0,0,0,0.1)",
  },
  dragHandleContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    padding: "20px 0 10px 0",
    cursor: "grab",
  },
  dragHandle: {
    width: "40px",
    height: "4px",
    backgroundColor: "#C4C4C4",
    borderRadius: "10px",
  },
  contentContainer: {
    flex: 1,
    position: "relative" as const,
    display: "flex",
    flexDirection: "column",
  },
  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    width: "100%",
    position: "fixed",
    top: 0,
    zIndex: 2,
  },
  chipContainer: {
    display: "flex",
    gap: "5%",
    mt: "1%",
    px: "4%",
  },
  accessibilityChip: {
    fontSize: { xs: "3.4vw", sm: 14 },
    fontWeight: 500,
    color: "#191919",
    "& .MuiChip-deleteIcon": {
      color: "#757575",
      "&:hover": {
        color: "#616161",
        opacity: 0.8,
      },
    },
  },
  openNowChip: {
    fontSize: { xs: "3.4vw", sm: 14 },
  },
  optionsDrawerPaper: {
    borderTopLeftRadius: "20px",
    borderTopRightRadius: "20px",
    p: "2%",
    maxHeight: "80vh",
    overflowY: "auto",
  },
  noResultsContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "50vh",
  },
  noResultsIcon: {
    fontSize: { xs: "9.71vw", sm: 40 },
    color: "#939393",
    mb: "2%",
  },
  noResultsText: {
    textAlign: "center",
    fontSize: { xs: "3.4vw", sm: 14 },
    color: "#191919",
  },
  actionBar: {
    position: "fixed",
    zIndex: 10000,
    bottom: 0,
    left: 0,
    right: 0,
    bgcolor: "#fff",
    px: "2%",
    pb: "2%",
    pt: "1%",
    borderTop: "1px solid #f0f0f0",
  },
};

const FilterDrawer: React.FC<FilterDrawerProps> = ({ userPosition }) => {
  const dispatch = useDispatch();
  const { selectedCategory: selectedFilter, isDrawerOpen } = useSelector(
    (state: RootState) => state.placeFilter,
  );
  const place = useSelector((state: RootState) => state.selectedPlace.place);
  const isOpen = isDrawerOpen;
  const selectedSubCategories = useMemo(
    () => selectedFilter?.subCategories || [],
    [selectedFilter?.subCategories]
  );
  const { api } = useApiClient();
  const [places, setPlaces] = useState<Place[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [openNow, setOpenNow] = useState(false);
  const [selectedAccessibility, setSelectedAccessibility] =
    useState<string>("all");
  const [view, setView] = useState<"list" | "details">("list");
  const [showAccessibilityOptions, setShowAccessibilityOptions] =
    useState(false);
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [currentSnapIndex, setCurrentSnapIndex] = useState(1); // Track current sheet snap point (0, 0.65, or 1)
  const drawerRef = useRef<HTMLDivElement>(null);
  const actionBarRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => {
    dispatch(clearPlaceFilter());
    dispatch(clearSelectedPlace());
    dispatch(setDrawerOpen(false));
    setOpenNow(false);
    setSelectedAccessibility("all");
    setView("list");
    setPage(1);
    setHasMore(true);
    setPlaces([]);
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        isOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(target) &&
        (!actionBarRef.current || !actionBarRef.current.contains(target))
      ) {
        const isClickInMuiMenu = (event.target as Element).closest(
          ".MuiPopover-root, .MuiDialog-root, .MuiDrawer-root, .MuiModal-root, .Toastify",
        );
        if (!isClickInMuiMenu) {
          handleClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, handleClose]);

  const fetchPlaces = useCallback(async (pageParam = 1) => {
    if (!userPosition) return;
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        lat: userPosition.coords.latitude,
        lng: userPosition.coords.longitude,
        radius: selectedFilter?.radius || 10000,
        limit: 10,
        page: pageParam,
      };

      if (selectedSubCategories?.length) {
        params.subcategories = selectedSubCategories.join(",");
      }

      if (selectedAccessibility !== "all") {
        params.accessibility = selectedAccessibility;
      }

      if (openNow) {
        params.open_now = true;
        params.local_time = dayjs().format("YYYY-MM-DDTHH:mm:ss");
      }

      const res = await api.get(
        ENDPOINTS.PLACES.FETCH_BY_CATEGORY_AND_BOUNDS,
        params,
      );
      const { places: newPlaces, total } = res?.data?.data || [];

      let combinedPlaces = newPlaces;
      
      if (pageParam === 1 && userPosition) {
        const lat = userPosition.coords.latitude;
        const lng = userPosition.coords.longitude;
        const cacheKey = `google_nearby_${lat.toFixed(3)}_${lng.toFixed(3)}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          const googleNearbyPlaces = JSON.parse(cachedData);
          const existingDbPlaceIds = new Set(newPlaces.map((p: Place) => p.g_place_id).filter(Boolean));
          
          const filteredGooglePlaces = googleNearbyPlaces
            .filter((gp: GoogleNearbyPlace) => gp.location && !existingDbPlaceIds.has(gp.id))
            .filter((gp: GoogleNearbyPlace) => {
              if (!selectedSubCategories || selectedSubCategories.length === 0) return true;
              const googleTypes = gp.types || [];
              return selectedSubCategories.some((sub: string) => googleTypes.includes(sub));
            })
            .map((gp: GoogleNearbyPlace) => {
              return {
                place_id: "",
                name: gp.displayName?.text || "Unnamed place",
                address_text: gp.formattedAddress || "",
                g_place_id: gp.id,
                coordinates: { lat: gp.location!.latitude, lng: gp.location!.longitude },
                is_active: true,
                overall_accessibility_score: 0,
                accessibility_status: "no_data",
                category_id: gp.types_mapped?.category_id || "",
                category: gp.types_mapped?.category || "",
                subcategory_id: gp.types_mapped?.subcategory_id || "",
                subcategory: gp.types_mapped?.subcategory || "",
              } as Place;
            });
            
          combinedPlaces = [...newPlaces, ...filteredGooglePlaces];
        }
      }

      setHasMore(total > pageParam * 10);

      setPlaces((prev) =>
        pageParam === 1 ? combinedPlaces : [...prev, ...newPlaces],
      );
    } catch (error) {
      if (pageParam === 1) setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [api, userPosition, selectedFilter, selectedSubCategories, selectedAccessibility, openNow]);

  const fetchPlace = useCallback(async () => {
    setLoading(true);
    setView("details");
    try {
      const res = await api.get(ENDPOINTS.PLACES.GET_BY_ID, {
        g_place_id: place?.g_place_id,
        lat: place?.coordinates?.lat,
        lng: place?.coordinates?.lng,
      });
      setSelectedPlace(res?.data?.data || null);
    } catch (error) {
      if (place?.g_place_id && !place?.place_id) {
        if (!place?.address_text) {
          try {
          const detailsRes = await api.get(
            ENDPOINTS.PLACES.GOOGLE_DETAILS(place.g_place_id),
            {
              fields: [
                "id",
                "displayName",
                "formattedAddress",
                "location",
                "types",
                "addressComponents",
              ].join(","),
            },
          );
          const googlePlace = detailsRes?.data?.data;
          if (googlePlace) {
            const mapped = googlePlace.types_mapped || {
              category_id: place.category_id ?? null,
              category: place.category ?? "Other",
              subcategory_id: place.subcategory_id ?? null,
              subcategory: place.subcategory ?? "Other",
            };
            const { country_code, city, postcode } =
              googlePlace.address_components_mapped || {
                country_code: "",
                city: "",
                postcode: "",
              };

            setSelectedPlace({
              g_place_id: googlePlace.id,
              name: googlePlace.displayName?.text || googlePlace.displayName || place.name,
              address_text: googlePlace.formattedAddress,
              coordinates: {
                lat: googlePlace.location?.latitude || place.coordinates?.lat || 0,
                lng: googlePlace.location?.longitude || place.coordinates?.lng || 0,
              },
              country_code,
              city,
              postcode,
              category_id: mapped.category_id ?? undefined,
              category: mapped.category,
              subcategory_id: mapped.subcategory_id ?? undefined,
              subcategory: mapped.subcategory,
            });
            return;
          }
          } catch (detailsError) {
            // Fall through to the plain fallback below.
          }
        }
      }
      setSelectedPlace(place || null);
    } finally {
      setLoading(false);
    }
  }, [api, place]);

  useEffect(() => {
    if (isOpen && selectedSubCategories?.length && !place) {
      setPage(1);
      setHasMore(true);
      fetchPlaces(1);
    }
  }, [isOpen, selectedSubCategories, selectedAccessibility, openNow, fetchPlaces, place]);

  useEffect(() => {
    if (isOpen && place) {
      fetchPlace();
    }
  }, [isOpen, place, fetchPlace]);

  const handleCloseButton = () => {
    // If on detail view, check if there's a filter context
    if (view === "details") {
      // If opened via filter selection, go back to list view
      if (selectedFilter && selectedSubCategories.length > 0) {
        setView("list");
        setSelectedPlace(null);
        dispatch(clearSelectedPlace());
      } else {
        // If opened directly (no filter), close the entire drawer
        handleClose();
      }
    } else {
      // If on list view, close the drawer
      handleClose();
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!loading && hasMore) {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchPlaces(nextPage);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (!place) {
        setView("list");
      }
      setShowAccessibilityOptions(false);
    }
  }, [isOpen, place]);

  const handlePlaceClick = (place: Place | null) => {
    setSelectedPlace(place);
    setView("details");
  };

  const activeConfig = ACCESSIBILITY_STATUS_CONFIG[selectedAccessibility || "no_data"];

  return (
    <>
      <Sheet
        isOpen={isOpen}
        onClose={handleClose}
        snapPoints={[0, 0.65, 1]}
        initialSnap={1}
        onSnap={(snapIndex) => setCurrentSnapIndex(snapIndex)}
        style={{ zIndex: 1200 }}
      >
        <Sheet.Container style={styles.sheetContainer} ref={drawerRef}>
          <Sheet.Header>
            <Box sx={styles.dragHandleContainer}>
              <Box sx={styles.dragHandle} />
            </Box>
            <IconButton
              onClick={handleCloseButton}
              size="small"
              sx={{ position: "absolute", right: 10, top: 10 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Sheet.Header>

          <Sheet.Content
            disableDrag={true}
            style={{
              paddingBottom: view === "details" ? "80px" : 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                ...styles.contentContainer,
                backgroundColor: places.length ? "#D9D9D9" : "#fff",
              }}
            >
              {/* Same content as before */}
              {view === "list" && (
                <Box>
                  <Box
                    sx={{
                      ...styles.listHeader,
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                    }}
                  >
                    <Box sx={styles.chipContainer}>
                      <Chip
                        label={
                          selectedAccessibility === "all"
                            ? "Accessibility Status"
                            : ACCESSIBILITY_STATUS_CONFIG[selectedAccessibility].label
                        }
                        onClick={() =>
                          setShowAccessibilityOptions(!showAccessibilityOptions)
                        }
                        onDelete={() =>
                          setShowAccessibilityOptions((prev) => !prev)
                        }
                        deleteIcon={<KeyboardArrowDownIcon fontSize="small" />}
                        sx={{
                          ...styles.accessibilityChip,
                          bgcolor: activeConfig ? "#F0DEFF" : "#f5f5f5",
                          border: activeConfig
                            ? `1px solid #F0DEFF`
                            : "1px solid #e0e0e0",
                        }}
                      />

                      <Chip
                        label="Open Now"
                        clickable
                        onClick={() => setOpenNow((prev) => !prev)}
                        sx={{
                          ...styles.openNowChip,
                          backgroundColor: openNow
                            ? "#F0DEFF !important"
                            : "#F1F1F1 !important",
                          border: openNow
                            ? "1px solid #D9D9D9 !important"
                            : "1px solid #D9D9D9 !important",
                        }}
                      />
                    </Box>
                  </Box>

                  <Drawer
                    anchor="bottom"
                    open={showAccessibilityOptions}
                    onClose={() => setShowAccessibilityOptions(false)}
                    sx={{ zIndex: 2400 }}
                    PaperProps={{
                      sx: styles.optionsDrawerPaper,
                    }}
                  >
                    <AccessibilityOptions
                      selectedAccessibility={selectedAccessibility}
                      onChange={(value) => {
                        setSelectedAccessibility(value);
                        setShowAccessibilityOptions(false);
                      }}
                    />
                  </Drawer>
                </Box>
              )}

              {view === "list" && (
                <Box
                  onScroll={handleScroll}
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    pb: currentSnapIndex === 1 && places.length > 1 ? { xs: "272px", sm: "10vw" } : 0, // Extra bottom padding ONLY in half-screen mode and when there is more than 1 place
                  }}
                >
                  {loading && page === 1 ? (
                    <Box sx={{ mt: "1%" }}>
                      {Array.from({ length: 3 }).map((_, i) => (
                        <GenericCardSkeleton key={i} hasMediaRow hasStatusLine={false} />
                      ))}
                    </Box>
                  ) : (
                    <>
                      {places?.map((place, index) => (
                        <React.Fragment key={place.g_place_id || place.place_id || index}>
                          <PlaceCard
                            place={place}
                            onClick={handlePlaceClick}
                            onClose={handleClose}
                            isCardView={view === "list"}
                          />
                        </React.Fragment>
                      ))}
                      {loading && page > 1 && (
                        <GenericCardSkeleton hasMediaRow hasStatusLine={false} />
                      )}
                      {places.length === 0 && !loading && (
                        <Box sx={styles.noResultsContainer}>
                          <RemoveCircleOutlineRounded
                            sx={styles.noResultsIcon}
                          />
                          <Typography variant="body2" sx={styles.noResultsText}>
                            No places {openNow ? "are currently open" : "found"}{" "}
                            for the selected category.
                          </Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              )}

              {view === "details" &&
                (loading ? (
                  <PlaceDetailsSkeleton />
                ) : (
                  <PlaceDetails
                    place={selectedPlace as Place}
                    sheetPosition={[0, 0.65, 1][currentSnapIndex]}
                  />
                ))}
            </Box>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>

      {/* Action Bar */}
      {view === "details" && selectedPlace && (
        <Box sx={styles.actionBar} ref={actionBarRef}>
          <ActionButtonGroup place={selectedPlace} onClose={handleClose} />
        </Box>
      )}
    </>
  );
};

export default FilterDrawer;
